/**
 * parseNombreTarea.js · v8.0.0 PRD Tareas V2
 *
 * El OS guarda los nombres con formato canónico "YYYYMMDD_Cliente_Titulo"
 * (helper backend `nombre-tarea.js`). Para el portal cliente eso es ruido:
 * el cliente no necesita ver la fecha en formato ISO ni el nombre del cliente
 * (ya está en el contexto). Este helper parsea el nombre crudo y devuelve
 * partes limpias que la UI compone como quiera.
 *
 * Contrato:
 *   parseNombreTarea("20260716_LaConsolidada_Presentacion 65 anos")
 *     → {
 *         titulo:      "Presentacion 65 anos",
 *         cliente:     "LaConsolidada",
 *         fechaISO:    "2026-07-16",
 *         fechaCorta:  "16-Jul",  // localized "es-PY"
 *         raw:         "20260716_LaConsolidada_Presentacion 65 anos",
 *       }
 *
 *   parseNombreTarea("Cualquier cosa sin patron")
 *     → { titulo: "Cualquier cosa sin patron", cliente: null, fechaISO: null, fechaCorta: null, raw: "..." }
 *
 * Fallback: si el nombre no matchea el patrón, retorna { titulo: raw } — la UI
 * nunca se queda en blanco.
 *
 * NO usa Gemini: regex heurístico cubre el 95% de los nombres del sistema
 * (todos los creados vía plan de contenido / importer canónico).
 */

const RE_CANONICO = /^(\d{8})_([^_]+)_(.+)$/;

// SOA 10 PRO · bug 7 — prefijo "AAAAMMDD_Cliente_" de los nombres crudos del
// backend. Se exporta aparte para los textos que NO pasan por
// parseNombreTarea (títulos de pedidos, mensajes, etc.).
const RE_PREFIJO = /^\d{8}_[^_]+_/;
// Variante con una etiqueta delante ("Ajuste: 20260921_Cliente_Título"), que es
// como se titulan los pedidos de ajuste creados desde Revisión / Feed.
const RE_PREFIJO_CON_ETIQUETA = /^([^:\d_]{1,20}:\s*)\d{8}_[^_]+_/;

/**
 * Quita el prefijo "AAAAMMDD_Cliente_" de cualquier texto que el cliente vaya
 * a leer como título ("20260921_La Consolidada_Cartel" → "Cartel"). Si no hay
 * prefijo devuelve el texto tal cual; los "_" restantes pasan a espacio solo
 * cuando hubo prefijo (no se tocan títulos libres con guion bajo).
 */
export function limpiarTitulo(texto) {
  // "NUEVA-TAREA" es un marcador interno de tareas creadas desde un pedido: el cliente no lo lee.
  const raw = String(texto ?? "").replace(/^\s*NUEVA[-_ ]TAREA[\s:_-]*/i, "").trim();
  if (RE_PREFIJO_CON_ETIQUETA.test(raw)) return raw.replace(RE_PREFIJO_CON_ETIQUETA, "$1").replace(/_/g, " ").trim();
  if (!RE_PREFIJO.test(raw)) return raw;
  return raw.replace(RE_PREFIJO, "").replace(/_/g, " ").trim();
}

const MESES_CORTOS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

/**
 * @param {string} nombre — nombre crudo tal como viene del backend
 * @returns {{titulo:string, cliente:string|null, fechaISO:string|null, fechaCorta:string|null, raw:string}}
 */
export function parseNombreTarea(nombre) {
  const raw = String(nombre || "").trim();
  if (!raw) return { titulo: "", cliente: null, fechaISO: null, fechaCorta: null, raw };

  const m = raw.match(RE_CANONICO);
  if (!m) return { titulo: raw, cliente: null, fechaISO: null, fechaCorta: null, raw };

  const [, fechaStr, cliente, tituloRaw] = m;
  const y = fechaStr.slice(0, 4);
  const mo = fechaStr.slice(4, 6);
  const d = fechaStr.slice(6, 8);
  const moIdx = parseInt(mo, 10) - 1;

  // fecha válida?
  const dNum = parseInt(d, 10);
  const yNum = parseInt(y, 10);
  const valid = moIdx >= 0 && moIdx < 12 && dNum >= 1 && dNum <= 31 && yNum >= 2020 && yNum <= 2100;

  return {
    titulo:     tituloRaw.replace(/_/g, " ").trim(),
    cliente,
    fechaISO:   valid ? `${y}-${mo}-${d}` : null,
    fechaCorta: valid ? `${dNum}-${MESES_CORTOS[moIdx]}` : null,
    raw,
  };
}

/**
 * Compone el sub-texto para la card según el PRD:
 *   "[Formato: Documento / Presentación] • Publica: 16-Jul."
 *
 * @param {object} tarea — objeto tarea del backend (con tipo_contenido y nombre)
 * @returns {string} sub-texto pintable
 */
export function subtextoTarea(tarea) {
  const parsed = parseNombreTarea(tarea?.nombre);
  const partes = [];
  if (tarea?.tipo_contenido) partes.push(`Formato: ${tarea.tipo_contenido}`);
  if (parsed.fechaCorta) partes.push(`Publica: ${parsed.fechaCorta}`);
  return partes.join(" • ");
}


/**
 * Limpia el texto de un brief para el cliente: quita las líneas decorativas del OS
 * ("─── Pedido original del cliente ───" pasa a "Pedido original del cliente"), los renglones que
 * son solo separadores y el prefijo interno "BRIEF · AAAAMMDD_Cliente_".
 */
export function limpiarBrief(texto) {
  return String(texto ?? "")
    .split("\n")
    .map(l => l.replace(/^\s*BRIEF\s*[·:-]\s*\d{8}_[^_]+_/i, "").replace(/^[\s─—=_-]{2,}\s*(.+?)\s*[─—=_-]{2,}\s*$/, "$1"))
    .filter(l => !/^[\s─—=_*-]*$/.test(l) || l.trim() === "")
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
